import { useContext, useState } from 'react'
import styled from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import StoreSortModal from './StoreSortModal'
import { LandingContext } from '../../LandingContext'
import { StoreRankingCriteria } from '../../types'
import { createReadableDateString, formatTimeString } from 'shop/utils'
import { useMediaQueries } from 'shop/hooks'
import { getFlexibilityValue } from './DateTimeFlexibilityButtons'

enum SortByLabels {
  DELIVERY = 'Delivery',
  PICKUP = 'Pickup',
  ALL = 'Delivery & Pickup'
}

const getSortByLabel = (
  sortCriteria: StoreRankingCriteria,
  dateFormatLength: 'short' | 'long' = 'long'
) => {
  const { fulfillmentTypes, fulfillmentDate, fulfillmentTime } = sortCriteria
  let result = {
    for: SortByLabels['ALL'],
    when: 'Earliest'
  }
  if (fulfillmentTypes.length === 1) {
    result = {
      ...result,
      for: SortByLabels[fulfillmentTypes[0]]
    }
  }

  if (fulfillmentDate) {
    result = {
      ...result,
      when: createReadableDateString(
        new Date(fulfillmentDate),
        dateFormatLength
      )
    }
  }

  if (fulfillmentTime) {
    result = {
      ...result,
      when: `${result.when} ${formatTimeString(fulfillmentTime, 'HH:mm')}`
    }
  }

  return result
}

type Props = {
  isControlHeaderStuck: boolean
}

const StoreSortControl = ({ isControlHeaderStuck }: Props) => {
  const { sortCriteria } = useContext(LandingContext)
  const [showSortModal, setShowSortModal] = useState<boolean>(false)
  const { isMobile } = useMediaQueries()

  return (
    <>
      {showSortModal && (
        <StoreSortModal handleClose={() => setShowSortModal(false)} />
      )}
      <Container>
        <SortSummaryContainer>
          <SortSummary isControlHeaderStuck={isControlHeaderStuck}>
            <span>For</span>
            <span>{getSortByLabel(sortCriteria).for}</span>
          </SortSummary>
          <SortSummary isControlHeaderStuck={isControlHeaderStuck}>
            <span>When</span>
            <span>
              {getSortByLabel(sortCriteria, isMobile ? 'short' : 'long').when}
              {sortCriteria?.flexibilitySeconds && (
                <>
                  {isMobile && <br />} &plusmn;{' '}
                  {getFlexibilityValue(sortCriteria.flexibilitySeconds)}
                </>
              )}
            </span>
          </SortSummary>
        </SortSummaryContainer>
        <ChangeButton onClick={() => setShowSortModal(true)}>
          Change
        </ChangeButton>
      </Container>
    </>
  )
}

const Container = styled.div<StyledHTMLElement>(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  transition: 'all 200ms ease-in'
}))

const SortSummaryContainer = styled.div<StyledHTMLElement>(() => ({
  display: 'flex',
  gap: '16px',

  '>div': {
    paddingLeft: '16px',
    borderLeft: '1px solid #D9D9D9'
  },
  '>div:first-of-type': {
    paddingLeft: '0',
    borderLeft: 'none'
  }
}))

const SortSummary = styled.div<
  StyledHTMLElement & { isControlHeaderStuck: boolean }
>(({ theme, isControlHeaderStuck }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: isControlHeaderStuck ? 0 : '4px',
  fontSize: '14px',
  lineHeight: '14px',
  fontFamily: theme.fonts['body'].family,
  fontWeight: 400,
  color: '#2A2A2A',

  '>span:first-of-type': {
    transition: 'all 200ms ease-in',
    height: isControlHeaderStuck ? 0 : '14px',
    opacity: isControlHeaderStuck ? 0 : 1
  },

  '>span:last-of-type': {
    fontWeight: 600
  }
}))

const ChangeButton = styled.div<StyledHTMLElement>(({ theme }) => ({
  padding: '8px 12px',
  borderRadius: '8px',
  backgroundColor: '#F5F5F5',
  cursor: 'pointer',

  fontSize: '14px',
  fontWeight: 600,
  fontFamily: theme.fonts['body'].family,

  // disable blue hightlight of user clicks
  WebkitTapHighlightColor: 'transparent',
  WebkitTouchCallout: 'none',
  WebkitUserSelect: 'none',
  KhtmlUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
  userSelect: 'none',

  '&:hover': {
    opacity: 0.8
  },
  '&:active': {
    opacity: 0.6
  }
}))

export default StoreSortControl
